interface IAbortController {
  [key: string]: AbortController;
}

interface IAbortService {
  getSignal(controllerName?: string): AbortSignal;
  singleInstanceAbortOnTimeout(timeoutInSeconds: number): AbortSignal;
  abortAll(): void;
}

/**
 * A factory class for creating AbortControllers and their associated signals.
 */
export default class AbortService implements IAbortService {
  private abortController: IAbortController;

  private static instance: AbortService;

  /**
   * Creates a new instance of AbortService.
   */
  private constructor() {
    this.abortController = {};
  }

  /**
   * Returns the singleton instance of AbortService.
   * @returns The singleton instance of AbortService.
   */
  public static getInstance() {
    if (!AbortService.instance) {
      AbortService.instance = new AbortService();
    }
    return AbortService.instance;
  }

  /**
   * Gets the AbortSignal associated with the specified controller name.
   * If a controller with the specified name already exists, its signal is aborted and a new controller is created.
   * @param controllerName - The name of the controller to get the signal for.
   * @returns The AbortSignal associated with the specified controller name.
   */
  public getSignal(controllerName = ""): AbortSignal {
    if (this.abortController[controllerName]) {
      this.abortController[controllerName].abort();
      this.abortController[controllerName] = new AbortController();
    } else {
      this.abortController[controllerName] = new AbortController();
    }
    return this.abortController[controllerName].signal;
  }

  /**
   * Creates a single instance of AbortController and returns its signal.
   * The instance is automatically aborted after the specified timeout.
   * @param timeoutInSeconds - The timeout in seconds after which the AbortController instance should be aborted.
   * @returns The signal of the created AbortController instance.
   */
  public singleInstanceAbortOnTimeout(timeoutInSeconds: number) {
    const controllerID = crypto.randomUUID();
    this.abortController[controllerID] = new AbortController();
    setTimeout(() => {
      this.abortController[controllerID].abort();
      delete this.abortController[controllerID];
    }, timeoutInSeconds * 1000);
    return this.abortController[controllerID].signal;
  }

  /**
   * Aborts all the ongoing API calls by aborting all the AbortController instances.
   */
  public abortAll(): void {
    Object.keys(this.abortController).forEach((key) => {
      this.abortController[key].abort();
      delete this.abortController[key];
    });
  }
}
